import { combineReducers } from 'redux';
import Enums from 'utils/EnumsManager';
import files from 'views/ReportListPanel/flow/reducer';
import {
  ADD_FOLDER,
  COMMIT_FOLDER_NAME,
  DELETE_FOLDER,
  DELETE_LOCAL_FOLDER,
  RESET_EDITING_FOLDER,
  SET_ACTIVE_FOLDER,
  SET_ACTIVE_TAB,
  SET_EDITING_FOLDER,
  SYNC_FOLDER
} from 'views/ReportListSider/flow/actionTypes';
import { REMOVE_SHARED_OBJECT } from 'views/ReportListPanel/flow/actionTypes';
import { Folder } from '../utils';
import {
  SET_DASHBOARD_FOLDERS,
  SET_REPORT_FOLDERS,
  RESET_REPORTLIST_STORE
} from './actionTypes';

import {
  SET_SELECTED_USERS,
  ADD_TEAM_TO_SHARED,
  ADD_USER_TO_SHARED
} from 'views/Section_SharedTo/flow/actionTypes';

const { FOLDER_TYPES, FOLDER_TYPES_IN_ARRAY } = Enums.REPORTS;
const { REPORT, DASHBOARD } = FOLDER_TYPES;
const initialState = {
  activeTab: REPORT,
  [REPORT]: [],
  [DASHBOARD]: []
};

const folders = (state = initialState, action) => {
  switch (action.type) {
    case SET_DASHBOARD_FOLDERS:
      const ownDashboards = action.payload.rawData.own.data.map(
        f =>
          new Folder({
            ...f,
            sharedToTeams: f.shared_to_teams,
            sharedToUsers: f.shared_to_users,
            type: action.payload.type
          })
      );
      const othersDashboards = action.payload.rawData.shared_by_others.data.map(
        f =>
          new Folder({
            ...f,
            sharedBy: f.belonged_user && f.belonged_user.name,
            sharedToTeams: f.shared_to_teams,
            sharedToUsers: f.shared_to_users,
            type: action.payload.type
          })
      );
      return {
        ...state,
        dashboard: [...ownDashboards, ...othersDashboards]
      };

    case SET_REPORT_FOLDERS:
      const ownFolders = action.payload.rawData.own.data.map(
        f =>
          new Folder({
            ...f,
            sharedToTeams: f.shared_to_teams,
            sharedToUsers: f.shared_to_users,
            type: action.payload.type
          })
      );
      const othersFolders = action.payload.rawData.shared_by_others.data.map(
        f =>
          new Folder({
            ...f,
            sharedBy: f.belonged_user && f.belonged_user.name,
            sharedToTeams: f.shared_to_teams,
            sharedToUsers: f.shared_to_users,
            type: action.payload.type
          })
      );
      return {
        ...state,
        report: [...ownFolders, ...othersFolders]
      };

    case SET_ACTIVE_FOLDER:
      // Notes: at one time, only one folder is active for all types.
      const { id, type } = action.payload;
      const reportDataAfterSelection = state.report.map(f => {
        f.isActive = f.id === id && f.type === type;
        return f;
      });
      const dashboardDataAfterSelection = state.dashboard.map(f => {
        f.isActive = f.id === id && f.type === type;
        return f;
      });
      return {
        ...state,
        report: reportDataAfterSelection,
        dashboard: dashboardDataAfterSelection
      };

    case SET_EDITING_FOLDER:
      const dataWithEditingFolder = state[action.payload.type].map(f => {
        f.isEditing = f.id === action.payload.id;
        return f;
      });
      return {
        ...state,
        [action.payload.type]: dataWithEditingFolder
      };

    case RESET_EDITING_FOLDER:
      const editingFolder = state[action.payload.type].find(
        f => f.id === action.payload.id
      );
      if (editingFolder) {
        editingFolder.isEditing = false;
      }
      return {
        ...state,
        [action.payload.type]: [...state[action.payload.type]]
      };

    case COMMIT_FOLDER_NAME:
      const dataWithNewName = state[action.payload.type].map(f => {
        if (f.id === action.payload.id) {
          f.name = action.payload.newName;
          f.isEditing = false;
        }
        return f;
      });
      return {
        ...state,
        [action.payload.type]: dataWithNewName
      };

    case DELETE_FOLDER:
      return {
        ...state,
        [action.payload.type]: state[action.payload.type].filter(
          f => f.id !== action.payload.id
        )
      };

    case ADD_FOLDER:
      const newFolder = new Folder({
        isEditing: true,
        type: action.payload.type
      });
      return {
        ...state,
        [action.payload.type]: [newFolder, ...state[action.payload.type]]
      };

    case SYNC_FOLDER:
      // reset other folders' state
      const dataWithDefaultActiveState = state[action.payload.type]
        .filter(f => f.id > 0)
        .map(f => {
          f.isActive = false;
          return f;
        });
      const committedFolder = new Folder({
        ...action.payload.data,
        isActive: true,
        isEditing: false,
        type: action.payload.type
      });
      return {
        ...state,
        [action.payload.type]: [committedFolder, ...dataWithDefaultActiveState]
      };

    case DELETE_LOCAL_FOLDER:
      return {
        ...state,
        [action.payload.type]: state[action.payload.type].filter(f => f.id > 0)
      };

    case SET_ACTIVE_TAB:
      const dataAfterClearActive = {
        activeTab: action.payload.tab
      };
      FOLDER_TYPES_IN_ARRAY.forEach(type => {
        dataAfterClearActive[type] = state[type].map(f => {
          f.isActive = false;
          return f;
        });
      });
      return {
        ...state,
        ...dataAfterClearActive
      };

    case RESET_REPORTLIST_STORE:
      return initialState;

    case REMOVE_SHARED_OBJECT:
      const { removedId, removedType } = action.payload;
      const sharedField =
        removedType === 'user' ? 'sharedToUsers' : 'sharedToTeams';
      return {
        ...state,
        [state.activeTab]: state[state.activeTab].map(f => {
          if (f.isActive) {
            f[sharedField] = f[sharedField].filter(
              shared => shared.id !== removedId
            );
          }
          return f;
        })
      };

    case SET_SELECTED_USERS:
      return {
        ...state,
        [state.activeTab]: state[state.activeTab].map(f => {
          if (f.isActive) {
            f.sharedToUsers = action.payload.users;
          }
          return f;
        })
      };

    case ADD_TEAM_TO_SHARED:
      return {
        ...state,
        [state.activeTab]: state[state.activeTab].map(f => {
          if (f.isActive) {
            f.sharedToTeams = [...f.sharedToTeams, action.payload.team];
          }
          return f;
        })
      };

    case ADD_USER_TO_SHARED:
      return {
        ...state,
        [state.activeTab]: state[state.activeTab].map(f => {
          if (f.isActive) {
            f.sharedToUsers = [...f.sharedToUsers, action.payload.user];
          }
          return f;
        })
      };

    default:
      return state;
  }
};

export default combineReducers({
  folders,
  files
});



// WEBPACK FOOTER //
// ./src/views/ReportList/flow/reducer.js